// Bank feed setup per bank account. The actual fetch implementation lives
// outside (the platform admin wires the chosen provider). This page is the
// per-tenant credential store + status surface.
import { useEffect, useState, type FormEvent } from 'react';
import { Landmark, RefreshCw, Save, X, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';

const PROVIDERS = [
  { value: 'manual',    label: 'Manual (CSV upload)' },
  { value: 'stitch',    label: 'Stitch (FNB / Standard / ABSA / Nedbank / Capitec)' },
  { value: 'ozow',      label: 'Ozow' },
  { value: 'truelayer', label: 'TrueLayer' },
  { value: 'direct',    label: 'Direct bank API (custom)' },
];

interface Feed {
  id?: string; org_id?: string; bank_account_id: string;
  provider: string; username: string | null; encrypted_secret: string | null;
  external_account_id: string | null; status: string;
  last_synced_at: string | null; last_error: string | null;
}

export default function BankFeeds() {
  const { currentOrg } = useOrg();
  const [accounts, setAccounts] = useState<any[]>([]);
  const [feeds, setFeeds] = useState<Feed[]>([]);
  const [editing, setEditing] = useState<Feed | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    if (!currentOrg) return;
    const [ba, bf] = await Promise.all([
      supabase.from('dodobooks_bank_accounts').select('id,name,bank_name,account_number,currency').eq('org_id', currentOrg.id).eq('is_archived', false).order('name'),
      supabase.from('dodobooks_bank_feeds').select('*').eq('org_id', currentOrg.id),
    ]);
    setAccounts(ba.data ?? []);
    setFeeds((bf.data ?? []) as Feed[]);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [currentOrg]);

  function openFor(bankAccountId: string) {
    const existing = feeds.find((f) => f.bank_account_id === bankAccountId);
    setEditing(existing ? { ...existing } : {
      bank_account_id: bankAccountId, provider: 'manual', username: '', encrypted_secret: '',
      external_account_id: '', status: 'idle', last_synced_at: null, last_error: null,
    });
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!currentOrg || !editing) return;
    setBusy(true);
    const payload = { ...editing, org_id: currentOrg.id };
    if (editing.id) {
      await supabase.from('dodobooks_bank_feeds').update(payload).eq('id', editing.id);
    } else {
      await supabase.from('dodobooks_bank_feeds').insert(payload);
    }
    setBusy(false); setEditing(null); load();
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="h-page flex items-center gap-2"><Landmark size={22} /> Bank feeds</h1>
        <p className="text-sm text-text-mute mt-1">Configure how live transactions land in DodoBooks for each bank account. Credentials are stored encrypted at rest.</p>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-text-mute uppercase tracking-widest font-mono">
              <th className="px-3 py-2">Account</th>
              <th className="px-3 py-2">Bank</th>
              <th className="px-3 py-2">Feed provider</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Last sync</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => {
              const f = feeds.find((x) => x.bank_account_id === a.id);
              const prov = PROVIDERS.find((p) => p.value === (f?.provider ?? 'manual'))?.label ?? 'Manual';
              return (
                <tr key={a.id} className="border-t border-line">
                  <td className="px-3 py-2 font-semibold">{a.name}</td>
                  <td className="px-3 py-2 text-xs text-text-mute">{a.bank_name ?? '—'} · {a.account_number ?? '—'}</td>
                  <td className="px-3 py-2 text-xs">{prov}</td>
                  <td className="px-3 py-2 text-xs">
                    {f?.status === 'connected'
                      ? <span className="text-emerald inline-flex items-center gap-1"><CheckCircle size={12} /> connected</span>
                      : f?.status === 'error'
                      ? <span className="text-rust inline-flex items-center gap-1"><AlertCircle size={12} /> {f.last_error?.slice(0, 40) ?? 'error'}</span>
                      : <span className="text-text-mute">idle</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">{f?.last_synced_at ? new Date(f.last_synced_at).toLocaleString('en-ZA') : '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => openFor(a.id)} className="btn btn-secondary btn-sm"><RefreshCw size={12} /> {f ? 'Edit' : 'Configure'}</button>
                  </td>
                </tr>
              );
            })}
            {accounts.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-text-mute text-sm">Add bank accounts on the <strong>Bank</strong> page first.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <form onSubmit={save} className="card w-full max-w-lg my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="h-section text-base">Bank feed setup</h2>
              <button type="button" className="text-text-mute hover:text-text" onClick={() => setEditing(null)}><X size={16} /></button>
            </div>
            <div className="field">
              <label>Provider</label>
              <select value={editing.provider} onChange={(e) => setEditing({ ...editing, provider: e.target.value })}>
                {PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            {editing.provider !== 'manual' && (
              <>
                <div className="field"><label>Username / Account holder reference</label><input value={editing.username ?? ''} onChange={(e) => setEditing({ ...editing, username: e.target.value })} /></div>
                <div className="field"><label>API key / token / secret</label><input type="password" value={editing.encrypted_secret ?? ''} onChange={(e) => setEditing({ ...editing, encrypted_secret: e.target.value })} placeholder="••••••••" /></div>
                <div className="field"><label>External account ID (provider's ID for this account)</label><input value={editing.external_account_id ?? ''} onChange={(e) => setEditing({ ...editing, external_account_id: e.target.value })} /></div>
              </>
            )}
            <div className="text-xs text-text-mute mb-3">
              Once saved, your platform admin's sync job will use these credentials to pull transactions. Set provider to <strong>Manual</strong> to disable and fall back to CSV uploads.
            </div>
            <div className="flex gap-2">
              <button type="button" className="btn btn-ghost flex-1" onClick={() => setEditing(null)}>Cancel</button>
              <button type="submit" className="btn btn-primary flex-1" disabled={busy}><Save size={14} /> {busy ? 'Saving…' : 'Save feed'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
